import { useEffect, useRef, useState } from 'react';
import type { ScreenBundle } from '@noorview/core';
import { activeDesign, normalizeDesign, normalizeMasjid } from '@noorview/core';
import { Stage } from '@noorview/display';
import { backend } from '../data/store';
import { useDisplayLock } from '../app/displayLock';
import { ExitPad, RELEASE_MS } from '../app/ExitPad';
import { inTvApp, onTvBack, tv, tvInfo } from '../tv/bridge';
import { BootCard } from '../tv/BootCard';
import { slotKey } from '../app/displaySlot';
import { listenLabel, useKhutbah } from '../tv/useKhutbah';

/**
 * The screen app ("player"). In the sandbox each browser tab is a separate virtual display; the Android app will run
 * this same page. It keeps the last bundle on the device, so a screen restarts and keeps working without internet.
 */

const HEARTBEAT_MS = 10_000;
const store = backend.kind === 'sandbox' ? sessionStorage : localStorage;
// Each display a browser holds (see displaySlot.ts) keeps its own id, cached screen and exit PIN.
const DEVICE_KEY = slotKey('noorview.device');

function cachedBundle(deviceId: string): ScreenBundle | null {
  try { const s = store.getItem(slotKey(`noorview.bundle.${deviceId}`)); return s ? (JSON.parse(s) as ScreenBundle) : null; } catch { return null; }
}
function saveBundle(deviceId: string, b: ScreenBundle | null) {
  try { if (b) store.setItem(slotKey(`noorview.bundle.${deviceId}`), JSON.stringify(b)); else store.removeItem(slotKey(`noorview.bundle.${deviceId}`)); } catch { /* storage full: keep running from memory */ }
}

/** The exit PIN of the masjid this screen was last paired to: just enough to check a PIN (see pin.ts). */
type ExitPinOf = { id: string; exitPinHash: string | null };
const EXIT_PIN_KEY = slotKey('nv.screen.exitPin');
function readExitPin(): ExitPinOf | null {
  try { const v = JSON.parse(localStorage.getItem(EXIT_PIN_KEY) ?? 'null') as ExitPinOf | null; return v && typeof v.id === 'string' ? v : null; } catch { return null; }
}
function saveExitPin(v: ExitPinOf) { try { localStorage.setItem(EXIT_PIN_KEY, JSON.stringify(v)); } catch { /* ignore */ } }

export function DisplayPage() {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<Stage | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(() => { try { return store.getItem(DEVICE_KEY); } catch { return null; } });
  const [code, setCode] = useState<string | null>(null);
  const [bundle, setBundle] = useState<ScreenBundle | null>(() => (deviceId ? cachedBundle(deviceId) : null));
  const [info, setInfo] = useState(backend.kind === 'sandbox');
  const [error, setError] = useState('');
  const bundleRef = useRef(bundle);
  bundleRef.current = bundle;

  useEffect(() => { document.title = 'NoorView Display'; document.body.classList.add('display-body'); return () => document.body.classList.remove('display-body'); }, []);

  // Register, then fetch the bundle whenever the dashboard changes something for this device.
  useEffect(() => {
    let alive = true;
    let id = deviceId;
    const refresh = async () => {
      try {
        const r = await backend.device.register(id);
        if (!alive) return;
        if (r.deviceId !== id) { id = r.deviceId; try { store.setItem(DEVICE_KEY, r.deviceId); } catch { /* ignore */ } setDeviceId(r.deviceId); }
        setCode(r.code);
        const b = r.screenId ? await backend.device.bundle(r.deviceId) : null;
        if (!alive) return;
        const same = JSON.stringify({ ...b, generatedAt: 0 }) === JSON.stringify({ ...bundleRef.current, generatedAt: 0 });
        if (!same) { setBundle(b); saveBundle(r.deviceId, b); }
        setError('');
        await backend.device.heartbeat(r.deviceId, b?.version ?? null);
      } catch (e) {
        // Offline or the server is unreachable: keep showing the last bundle.
        setError(e instanceof Error ? e.message : 'Offline');
      }
    };
    let unwatch = () => {};
    const start = async () => { await refresh(); if (alive && id) unwatch = backend.device.watch(id, () => void refresh()); };
    void start();
    const hb = setInterval(() => { if (id) void backend.device.heartbeat(id, bundleRef.current?.version ?? null).catch(() => undefined); }, HEARTBEAT_MS);
    const poll = setInterval(() => void refresh(), 5 * 60_000); // belt and braces if a live message is ever missed
    return () => { alive = false; unwatch(); clearInterval(hb); clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render the bundle; switch designs when a schedule starts or ends.
  useEffect(() => {
    if (!bundle || !host.current) return;
    const masjid = normalizeMasjid(bundle.masjid);
    if (!stage.current) stage.current = new Stage(host.current, { mode: 'screen', masjid, timetable: bundle.timetable, quality: 'high' });
    const s = stage.current;
    s.setData(masjid, bundle.timetable);
    let current = '';
    const apply = () => {
      const a = activeDesign({ design: normalizeDesign(bundle.design), schedules: bundle.schedules.map((x) => ({ ...x, design: normalizeDesign(x.design) })) }, new Date());
      const key = a.scheduleId ?? 'main';
      if (key !== current) { current = key; s.setDesign(a.design); s.layout(); }
    };
    apply();
    s.setBroadcasts(bundle.broadcasts ?? []);
    s.setPower(bundle.screen.power);
    s.setOptions({ salahBlank: bundle.screen.salahBlank !== false, screenName: bundle.screen.name, screenId: bundle.screen.id });
    void document.fonts?.ready.then(() => s.refit());
    const t = setInterval(apply, 15_000);
    return () => clearInterval(t);
  }, [bundle]);

  useEffect(() => () => { stage.current?.destroy(); stage.current = null; }, []);
  const listen = listenLabel(useKhutbah(bundle, stage));
  // A paired screen is locked: nothing on the screen itself can take it out of NoorView. Unpairing releases it.
  // (Virtual screens in the sandbox and the demo stay free, so they can be closed.)
  // Touching the screen or pressing a key asks for the masjid's exit PIN (if it has one); the right PIN releases the
  // screen for two minutes.
  const [pad, setPad] = useState(false);
  const [releasedUntil, setReleasedUntil] = useState(0);
  const pinMasjid = bundle ? normalizeMasjid(bundle.masjid) : null;
  useEffect(() => { if (!releasedUntil) return; const t = setTimeout(() => setReleasedUntil(0), Math.max(0, releasedUntil - Date.now())); return () => clearTimeout(t); }, [releasedUntil]);
  const locked = !!bundle && backend.kind === 'supabase' && !releasedUntil;
  useDisplayLock(locked, { allow: pad, onInput: () => { if (!pad && pinMasjid?.exitPinHash) setPad(true); } });

  // In the NoorView TV app: the app keeps a locked screen open and in front, and reloads the page if it ever freezes.
  useEffect(() => { tv.setLocked(locked); }, [locked]);
  useEffect(() => { if (!inTvApp) return; tv.alive(); const t = setInterval(() => tv.alive(), 15_000); return () => clearInterval(t); }, []);
  // The remote's Back button: closes the PIN pad; on a locked screen asks for the exit PIN (or says how to free it);
  // otherwise closes the app.
  const [hint, setHint] = useState('');
  useEffect(() => { if (!hint) return; const t = setTimeout(() => setHint(''), 6000); return () => clearTimeout(t); }, [hint]);
  const app = inTvApp ? tvInfo() : null;
  // On a PC (NoorView Screen for Windows) the screen stays locked whether it's paired or not, and closes only with the
  // masjid's exit PIN: Esc opens the PIN pad, and the right PIN closes NoorView back to the desktop. The screen
  // remembers the PIN of the masjid it was last paired to (only what's needed to check it), so it still works after
  // the screen is unpaired. Only a screen with no PIN to check (never paired, or its masjid has none) asks
  // "Close NoorView?" instead.
  const pc = app?.platform === 'windows';
  const [closeAsk, setCloseAsk] = useState(false);
  const [lastPin, setLastPin] = useState<ExitPinOf | null>(() => (inTvApp ? readExitPin() : null));
  useEffect(() => {
    if (!inTvApp || !pinMasjid) return;
    const v: ExitPinOf = { id: pinMasjid.id, exitPinHash: pinMasjid.exitPinHash ?? null };
    if (v.id !== lastPin?.id || v.exitPinHash !== lastPin?.exitPinHash) { saveExitPin(v); setLastPin(v); }
  }, [pinMasjid?.id, pinMasjid?.exitPinHash]); // eslint-disable-line react-hooks/exhaustive-deps
  const exitMasjid = pinMasjid ?? lastPin;
  useEffect(() => onTvBack(() => {
    if (closeAsk) { setCloseAsk(false); return; }
    if (pad) { setPad(false); return; }
    if (locked) {
      if (pinMasjid?.exitPinHash) setPad(true);
      else setHint(pc
        ? 'This screen is locked. To close NoorView, set an exit PIN in the NoorView app (Settings › Screen Lock), then press Esc and enter it.'
        : 'This screen is locked. To close NoorView, unpair it in the NoorView app, or set an exit PIN in Settings › Screen Lock.');
      return;
    }
    if (releasedUntil) { tv.exit(); return; }
    if (pc) { if (exitMasjid?.exitPinHash) setPad(true); else setCloseAsk(true); return; }
    tv.exit();
  }), [pad, locked, pinMasjid?.exitPinHash, pc, releasedUntil, closeAsk, exitMasjid?.exitPinHash]);
  // Just paired (or moved to another screen): show which screen this is for a few seconds, so whoever is standing at
  // the TV can see it's the right one.
  const lastScreen = useRef<string | null | undefined>(undefined);
  const [paired, setPaired] = useState<{ screen: string; masjid: string; out: boolean } | null>(null);
  // The start-up card (TV app) waits for this: the screen, its pairing code, or "can't reach NoorView" is showing.
  useEffect(() => { if (bundle || code || error) window.dispatchEvent(new Event('noorview:ready')); }, [bundle, code, error]);
  useEffect(() => {
    const id = bundle?.screen.id ?? null;
    const before = lastScreen.current;
    lastScreen.current = id;
    if (before === undefined || !id || id === before || !bundle) return;
    setPaired({ screen: bundle.screen.name, masjid: bundle.masjid.name, out: false });
  }, [bundle]);
  // The card goes after a few seconds (timed on its own, so a screen that changes meanwhile doesn't keep it up).
  const pairedAt = paired?.screen ?? null;
  useEffect(() => {
    if (!pairedAt) return;
    const t1 = setTimeout(() => setPaired((p) => (p ? { ...p, out: true } : p)), 5000);
    const t2 = setTimeout(() => setPaired(null), 5600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pairedAt]);
  useEffect(() => {
    if (locked) { setInfo(false); return; }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'i') setInfo((v) => !v); if (e.key === 'f') void document.documentElement.requestFullscreen?.().catch(() => undefined); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [locked]);
  // Hide the mouse pointer when it isn't moving.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const move = () => { document.body.classList.remove('no-cursor'); clearTimeout(t); t = setTimeout(() => document.body.classList.add('no-cursor'), 2500); };
    window.addEventListener('pointermove', move);
    move();
    return () => { window.removeEventListener('pointermove', move); clearTimeout(t); };
  }, []);

  const isPaired = !!bundle;
  return (
    <div className="display">
      <div ref={host} className="display-stage" style={{ visibility: isPaired ? 'visible' : 'hidden' }} />
      {!isPaired && (
        <div className="pairing">
          <img src={`${import.meta.env.BASE_URL}noorview-logo.png`} alt="" width={96} height={96} />
          <h1><b>Noor</b>View</h1>
          {code ? (<>
            <p>To show your masjid’s screen here, open the NoorView app or dashboard, go to <strong>Screens</strong>, choose <strong>Pair display</strong> and enter this code:</p>
            <div className="code" aria-label={`Pairing code ${code.split('').join(' ')}`}>{code.split('').map((c, i) => <span key={i}>{c}</span>)}</div>
            <small>Waiting to be paired…</small>
            {app && !app.autostart && (
              <button type="button" className="tv-autostart" autoFocus onClick={() => tv.openAutostartSettings()}>
                To open NoorView by itself when the TV turns on, press OK and allow <b>Display over other apps</b>
              </button>
            )}
          </>) : <p>{error ? 'Can’t reach NoorView. Check the internet connection.' : 'Starting…'}</p>}
          {app && <small className="tv-version">NoorView {pc ? 'Screen' : 'TV'} {app.version} · {app.resolution}</small>}
        </div>
      )}
      {closeAsk && !isPaired && (
        <div className="exit-ask" role="dialog" aria-label="Close NoorView">
          <div className="exit-ask-card">
            <h2>Close NoorView?</h2>
            <p>This screen isn’t protected by an exit PIN yet. Once it’s paired and the masjid has an exit PIN, only the PIN will close it.</p>
            <div className="exit-ask-btns">
              <button type="button" autoFocus onClick={() => setCloseAsk(false)}>Keep showing</button>
              <button type="button" className="danger" onClick={() => tv.exit()}>Close NoorView</button>
            </div>
          </div>
        </div>
      )}
      {pad && exitMasjid && <ExitPad masjid={exitMasjid} onClose={() => setPad(false)} onRelease={() => {
        setPad(false);
        // On a PC the right PIN closes NoorView at once; a TV (or a browser) is unlocked for two minutes.
        if (pc || !pinMasjid) { tv.exit(); return; }
        setReleasedUntil(Date.now() + RELEASE_MS);
      }} />}
      {!!releasedUntil && (
        <div className="exit-released" role="status">
          <span>{pc ? 'Unlocked for 2 minutes. Press Esc to close NoorView.' : inTvApp ? 'Unlocked for 2 minutes. Press Back to close NoorView.' : 'Unlocked for 2 minutes. You can leave full screen or close NoorView now.'}</span>
          <button type="button" onClick={() => setReleasedUntil(0)}>Lock again</button>
        </div>
      )}
      {hint && <div className="exit-released" role="status"><span>{hint}</span></div>}
      {listen && <div className={`kh-listen${listen.bad ? ' bad' : ''}`} role="status">{listen.text}</div>}
      {paired && <BootCard line="Paired" title={paired.screen} sub={paired.masjid} out={paired.out} />}
      {info && (
        <div className="display-info" role="status">
          {backend.kind === 'sandbox' ? 'Virtual screen' : 'NoorView'}{bundle ? ` · ${bundle.screen.name} · v${bundle.version}` : ' · Not paired'}{error ? ' · Offline' : ''}{app ? ` · ${app.resolution}` : ''}
          <small> · press I to hide, F for full screen</small>
        </div>
      )}
    </div>
  );
}
